import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Copy, Code2 } from 'lucide-react';
import { toast } from 'sonner';

export const AdEmbedModal = ({ open, onOpenChange, ad }) => {
    const [width, setWidth] = useState(320);
    const [height, setHeight] = useState(520);
    const [theme, setTheme] = useState('light');

    if (!ad) return null;
    const base = (process.env.REACT_APP_BACKEND_URL || '');
    const embedUrl = `${base}/api/embed/${ad.id}?theme=${theme}`;
    const iframeCode = `<iframe src="${embedUrl}" width="${width}" height="${height}" style="border:0;border-radius:16px;overflow:hidden" loading="lazy" allowtransparency="true" title="${ad.title.replace(/"/g, '&quot;')}"></iframe>`;

    const copy = async () => {
        try { await navigator.clipboard.writeText(iframeCode); toast.success('Código copiado al portapapeles'); }
        catch { toast.error('No se pudo copiar'); }
    };

    // Scale preview to fit modal on small screens
    const previewScale = () => {
        if (typeof window === 'undefined') return 1;
        const availW = Math.min(window.innerWidth - 80, 560); // approx modal inner width
        return Math.min(1, availW / width);
    };
    const scale = previewScale();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
                data-testid="ad-embed-modal"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-base sm:text-lg"><Code2 className="h-5 w-5" />Compartir con código embebido</DialogTitle>
                    <DialogDescription className="text-xs sm:text-sm">Pégalo en tu blog, WordPress, Shopify, o red social que permita HTML.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 sm:space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                        <div>
                            <Label className="text-xs">Ancho (px)</Label>
                            <Input type="number" value={width} onChange={(e) => setWidth(Math.max(200, Math.min(1200, Number(e.target.value))))} data-testid="embed-width" className="h-9" />
                        </div>
                        <div>
                            <Label className="text-xs">Alto (px)</Label>
                            <Input type="number" value={height} onChange={(e) => setHeight(Math.max(300, Math.min(1000, Number(e.target.value))))} data-testid="embed-height" className="h-9" />
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                            <Label className="text-xs">Tema</Label>
                            <Select value={theme} onValueChange={setTheme}>
                                <SelectTrigger data-testid="embed-theme" className="h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="light">Claro</SelectItem>
                                    <SelectItem value="dark">Oscuro</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Preview - scales down on mobile */}
                    <div>
                        <Label className="text-xs">Previsualización</Label>
                        <div className="rounded-xl border p-2 sm:p-3 bg-secondary flex justify-center overflow-hidden">
                            <div
                                style={{
                                    width: width * scale,
                                    height: height * scale,
                                    overflow: 'hidden',
                                    position: 'relative',
                                }}
                            >
                                <iframe
                                    src={embedUrl}
                                    width={width}
                                    height={height}
                                    style={{
                                        border: 0,
                                        borderRadius: 16,
                                        background: theme === 'dark' ? '#0f0f0f' : '#fff',
                                        transform: `scale(${scale})`,
                                        transformOrigin: 'top left',
                                    }}
                                    loading="lazy"
                                    title="preview"
                                />
                            </div>
                        </div>
                        {scale < 1 && <p className="text-[10px] text-muted-foreground mt-1">Previsualización escalada al {Math.round(scale * 100)}% para caber en pantalla.</p>}
                    </div>

                    {/* Code */}
                    <div>
                        <Label className="text-xs">Código iframe</Label>
                        <Textarea readOnly value={iframeCode} rows={4} className="font-mono text-[11px] sm:text-xs break-all" data-testid="embed-code" />
                    </div>
                    <Button onClick={copy} className="w-full" data-testid="embed-copy"><Copy className="h-4 w-4 mr-2" />Copiar código</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
